import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { InvoiceCard } from "./InvoiceCard";
import { Button } from "@/components/ui/button";
import { Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Invoice = Database["public"]["Tables"]["invoices"]["Row"];

interface JobInvoicesProps {
  jobId: string;
  orgId: string;
  customerEmail?: string;
}

export const JobInvoices = ({ jobId, orgId, customerEmail }: JobInvoicesProps) => {
  const queryClient = useQueryClient();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");

  // Fetch invoices for this job
  const { data: invoices, isLoading } = useQuery({
    queryKey: ["invoices", jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Invoice[];
    },
  });

  // Generate invoice mutation
  const generateInvoice = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("generate-invoice", {
        body: { jobId, orgId },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", jobId] });
      toast.success("Invoice generated successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to generate invoice");
    },
  });

  // Send invoice mutation
  const sendInvoice = useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from("invoices")
        .update({
          status: "sent",
          sent_date: new Date().toISOString(),
        })
        .eq("id", invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", jobId] });
      toast.success("Invoice marked as sent");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send invoice");
    },
  });

  // Mark paid mutation
  const markPaid = useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from("invoices")
        .update({
          status: "paid",
          paid_date: new Date().toISOString(),
        })
        .eq("id", invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", jobId] });
      toast.success("Invoice marked as paid");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to mark invoice as paid");
    },
  });

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async ({ invoiceId, email, emailType }: { invoiceId: string; email: string; emailType: string }) => {
      const { error } = await supabase.functions.invoke("send-invoice-email", {
        body: { invoiceId, customerEmail: email, emailType },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", jobId] });
      toast.success("Email sent successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send email");
    },
  });

  // Record payment mutation
  const recordPayment = useMutation({
    mutationFn: async ({ invoiceId, amount, method }: { invoiceId: string; amount: number; method: string }) => {
      const { error } = await supabase
        .from("payment_history")
        .insert({
          invoice_id: invoiceId,
          org_id: orgId,
          amount,
          payment_method: method,
          payment_date: new Date().toISOString(),
        });
      if (error) throw error;

      // Check if fully paid
      const { data: invoice } = await supabase
        .from("invoices")
        .select("total_amount")
        .eq("id", invoiceId)
        .single();

      const { data: payments } = await supabase
        .from("payment_history")
        .select("amount")
        .eq("invoice_id", invoiceId);

      const totalPaid = payments?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;

      if (invoice && totalPaid >= invoice.total_amount) {
        await supabase
          .from("invoices")
          .update({
            status: "paid",
            paid_date: new Date().toISOString(),
          })
          .eq("id", invoiceId);

        // Send receipt email
        if (customerEmail) {
          await supabase.functions.invoke("send-invoice-email", {
            body: { invoiceId, customerEmail, emailType: "receipt" },
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", jobId] });
      setPaymentDialogOpen(false);
      setPaymentAmount("");
      toast.success("Payment recorded successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to record payment");
    },
  });

  // Download PDF (placeholder)
  const handleDownloadPDF = (invoiceId: string) => {
    toast.info("PDF download will be implemented soon");
    // TODO: Implement PDF generation
  };

  const handleSendEmail = (invoiceId: string, email: string) => {
    sendEmailMutation.mutate({ invoiceId, email, emailType: "invoice" });
  };

  const handleSendReminder = (invoiceId: string, email: string) => {
    sendEmailMutation.mutate({ invoiceId, email, emailType: "reminder" });
  };

  const handleRecordPayment = (invoiceId: string, _amount: number, _method: string) => {
    setSelectedInvoice(invoiceId);
    setPaymentDialogOpen(true);
  };

  const handleSubmitPayment = () => {
    if (!selectedInvoice || !paymentAmount) {
      toast.error("Please enter payment amount");
      return;
    }
    recordPayment.mutate({
      invoiceId: selectedInvoice,
      amount: parseFloat(paymentAmount),
      method: paymentMethod,
    });
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading invoices...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Invoices</h3>
        <Button
          onClick={() => generateInvoice.mutate()}
          disabled={generateInvoice.isPending}
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Generate Invoice
        </Button>
      </div>

      {!invoices || invoices.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h4 className="text-lg font-semibold mb-2">No invoices yet</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Generate an invoice to bill for this job
          </p>
          <Button
            onClick={() => generateInvoice.mutate()}
            disabled={generateInvoice.isPending}
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate Invoice
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              onSend={sendInvoice.mutate}
              onMarkPaid={markPaid.mutate}
              onDownloadPDF={handleDownloadPDF}
              onSendEmail={handleSendEmail}
              onSendReminder={handleSendReminder}
              onRecordPayment={handleRecordPayment}
              customerEmail={customerEmail}
            />
          ))}
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Enter the payment details below
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Payment Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="method">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleSubmitPayment} 
              disabled={recordPayment.isPending}
              className="w-full"
            >
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
